import React from 'react';
import { Button, Badge, ButtonGroup } from 'reactstrap';
import Loader from '../../components/Loader';
import { withTranslation } from '../../i18n/LanguageContext';

/**
 * StripeConnectBadge
 * 
 * Renders the Stripe connection status and action buttons for a single user.
 * Used inside the Users page Add/Edit drawer.
 * 
 * Props:
 *   - user: the user object (must have UserId, Email, Telephone, Username)
 *   - stripeAccount: the stripe account info from redux state (or null)
 *   - isConnecting: boolean
 *   - onDisconnect: fn(userId)
 *   - onRefreshStatus: fn(userId)
 *   - onViewDashboard: fn(userId)
 *   - onFixOnStripe: fn(userId)
 *   - onTogglePayout: fn(userId, enabled)
 *   - onSendEmail: fn(userId, merchantId, email, username)
 *   - onSendSms: fn(userId, merchantId, phone, username)
 *   - merchantId: number
 */
class StripeConnectBadge extends React.Component {

    state = {
        connectionMode: 'create',
        existingAccountId: '',
        linkError: ''
    };

    componentDidUpdate(prevProps) {
        if (prevProps.user?.UserId !== this.props.user?.UserId) {
            this.setState({
                connectionMode: 'create',
                existingAccountId: '',
                linkError: ''
            });
        }
    }

    getStatusBadge(status) {
        const t = this.props.t;
        switch (status) {
            case 'active':
                return <Badge color="success">{t('stripe.connected')}</Badge>;
            case 'pending':
            case 'pending_verification':
            case 'onboarding_incomplete':
                return <Badge color="warning">{t('stripe.pending')}</Badge>;
            case 'restricted':
                return <Badge color="danger">{t('stripe.restricted')}</Badge>;
            case 'disabled':
                return <Badge color="secondary">{t('stripe.disabled')}</Badge>;
            default:
                return <Badge color="light">{t('stripe.notConnected')}</Badge>;
        }
    }

    canSendEmail() {
        const { user } = this.props;
        return user && user.Email && user.Email.trim() !== '';
    }

    canSendSms() {
        const { user } = this.props;
        return user && user.Telephone && user.Telephone.trim() !== '';
    }

    isTipPayoutDisabled() {
        return Number(this.props.tipPayoutRoutingMode) === 3;
    }

    getAccountLinkSourceLabel(source) {
        const t = this.props.t;
        switch (source) {
            case 'linked_existing':
                return t('stripe.linkedExisting');
            case 'created_platform':
                return t('stripe.platformCreated');
            default:
                return '';
        }
    }

    handleExistingAccountChange = (event) => {
        this.setState({
            existingAccountId: event.target.value,
            linkError: ''
        });
    };

    handleLinkExistingAccount = () => {
        const { user } = this.props;
        const normalizedAccountId = (this.state.existingAccountId || '').trim();

        if (!normalizedAccountId) {
            this.setState({ linkError: this.props.t('stripe.enterAccountId') });
            return;
        }

        if (!normalizedAccountId.startsWith('acct_')) {
            this.setState({ linkError: this.props.t('stripe.acctPrefix') });
            return;
        }

        this.setState({ linkError: '' });
        this.props.onLinkExisting(user.UserId, normalizedAccountId);
    };

    renderInitialConnectionActions() {
        const { user, merchantId, t } = this.props;
        const { connectionMode, existingAccountId, linkError } = this.state;

        if (this.isTipPayoutDisabled()) {
            return (
                <p className="mb-0 text-muted" style={{fontSize: '0.85rem'}}>
                    {t('stripe.disabledHelp')}
                </p>
            );
        }

        return (
            <div>
                <p className="mb-2" style={{fontSize: '0.85rem', color: '#848792'}}>
                    {t('stripe.chooseHow')}
                </p>
                <ButtonGroup size="sm" className="mb-2">
                    <Button
                        color="info"
                        outline={connectionMode !== 'create'}
                        active={connectionMode === 'create'}
                        aria-pressed={connectionMode === 'create'}
                        onClick={() => this.setState({ connectionMode: 'create', linkError: '' })}
                    >
                        {t('stripe.createNew')}
                    </Button>
                    <Button
                        color="info"
                        outline={connectionMode !== 'existing'}
                        active={connectionMode === 'existing'}
                        aria-pressed={connectionMode === 'existing'}
                        onClick={() => this.setState({ connectionMode: 'existing', linkError: '' })}
                    >
                        {t('stripe.linkExisting')}
                    </Button>
                </ButtonGroup>

                {connectionMode === 'create' ? (
                    <div>
                        <p className="mb-2" style={{fontSize: '0.85rem', color: '#848792'}}>
                            {t('stripe.sendOnboard')}
                        </p>
                        <div style={{display: 'flex', gap: '8px'}}>
                            <Button
                                size="sm"
                                color="info"
                                disabled={!this.canSendEmail()}
                                title={this.canSendEmail() ? t('stripe.sendTo', { dest: user.Email }) : t('stripe.noEmail')}
                                onClick={() => this.props.onSendEmail(user.UserId, merchantId, user.Email, user.Username)}
                            >
                                <i className="zmdi zmdi-email mr-1"></i>
                                {t('stripe.emailLink')}
                            </Button>
                            <Button
                                size="sm"
                                color="success"
                                disabled={!this.canSendSms()}
                                title={this.canSendSms() ? t('stripe.sendTo', { dest: user.Telephone }) : t('stripe.noPhone')}
                                onClick={() => this.props.onSendSms(user.UserId, merchantId, user.Telephone, user.Username)}
                            >
                                <i className="zmdi zmdi-smartphone mr-1"></i>
                                {t('stripe.textLink')}
                            </Button>
                        </div>
                        {!this.canSendEmail() && !this.canSendSms() && (
                            <p className="text-warning mt-2" style={{fontSize: '0.75rem'}}>
                                {t('stripe.addContact')}
                            </p>
                        )}
                    </div>
                ) : (
                    <div>
                        <p className="mb-2" style={{fontSize: '0.85rem', color: '#848792'}}>
                            {t('stripe.linkExistingHelp')}
                        </p>
                        <input
                            type="text"
                            className="form-control"
                            value={existingAccountId}
                            onChange={this.handleExistingAccountChange}
                            placeholder="acct_1234..."
                        />
                        {linkError && (
                            <p className="text-danger mt-2 mb-0" style={{fontSize: '0.75rem'}}>
                                {linkError}
                            </p>
                        )}
                        <Button
                            size="sm"
                            color="info"
                            className="mt-2"
                            onClick={this.handleLinkExistingAccount}
                        >
                            {t('stripe.linkAccount')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    renderResendButtons() {
        const { user, merchantId, t } = this.props;

        if (this.isTipPayoutDisabled()) {
            return (
                <div className="mb-2">
                    <p className="mb-2 text-muted" style={{fontSize: '0.8rem'}}>
                        {t('stripe.disabledOnboard')}
                    </p>
                    <Button
                        color="link"
                        size="sm"
                        className="px-0"
                        onClick={() => this.props.onRefreshStatus(user.UserId)}
                    >
                        <i className="zmdi zmdi-refresh mr-1"></i>
                        {t('stripe.refresh')}
                    </Button>
                </div>
            );
        }

        return (
            <div className="mb-2">
                <p className="mb-2" style={{fontSize: '0.8rem', color: '#848792'}}>
                    {t('stripe.resend')}
                </p>
                <ButtonGroup size="sm">
                    <Button
                        color="info"
                        disabled={!this.canSendEmail()}
                        title={this.canSendEmail() ? t('stripe.sendTo', { dest: user.Email }) : t('stripe.noEmail')}
                        onClick={() => this.props.onSendEmail(user.UserId, merchantId, user.Email, user.Username)}
                    >
                        <i className="zmdi zmdi-email mr-1"></i>
                        {t('stripe.email')}
                    </Button>
                    <Button
                        color="success"
                        disabled={!this.canSendSms()}
                        title={this.canSendSms() ? t('stripe.sendTo', { dest: user.Telephone }) : t('stripe.noPhone')}
                        onClick={() => this.props.onSendSms(user.UserId, merchantId, user.Telephone, user.Username)}
                    >
                        <i className="zmdi zmdi-smartphone mr-1"></i>
                        {t('stripe.text')}
                    </Button>
                </ButtonGroup>
                <Button
                    color="link"
                    size="sm"
                    className="ml-2"
                    onClick={() => this.props.onRefreshStatus(user.UserId)}
                >
                    <i className="zmdi zmdi-refresh mr-1"></i>
                    {t('stripe.refresh')}
                </Button>
            </div>
        );
    }

    renderCancelButton() {
        const { user } = this.props;
        return (
            <div className="mt-2" style={{borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px'}}>
                <Button
                    color="danger"
                    size="sm"
                    outline
                    onClick={() => {
                        if (window.confirm(this.props.t('stripe.removeConfirm'))) {
                            this.props.onDisconnect(user.UserId);
                        }
                    }}
                >
                    <i className="zmdi zmdi-close mr-1"></i>
                    {this.props.t('stripe.removeAccount')}
                </Button>
            </div>
        );
    }

    render() {
        const { user, stripeAccount, isConnecting, merchantId } = this.props;
        const status = stripeAccount ? stripeAccount.stripeAccountStatus : 'not_connected';
        const hasAccount = stripeAccount && stripeAccount.stripeAccountId;
        const tipPayoutEnabled = stripeAccount ? stripeAccount.tipPayoutEnabled : false;
        const sourceLabel = this.getAccountLinkSourceLabel(stripeAccount && stripeAccount.accountLinkSource);

        return (
            <div className="stripe-connect-section">
                <label className="d-block mb-2">{this.props.t('stripe.heading')}</label>

                <div className="d-flex align-items-center flex-wrap mb-2">
                    {this.getStatusBadge(status)}
                    {hasAccount && (
                        <small className="ml-2 text-muted">
                            {stripeAccount.stripeAccountId}
                        </small>
                    )}
                    {hasAccount && sourceLabel && (
                        <small className="ml-2 text-muted">
                            {sourceLabel}
                        </small>
                    )}
                </div>

                {isConnecting ? (
                    <Loader size={24} />
                ) : (
                    <div className="stripe-connect-actions">
                        {/* No account at all — initial send */}
                        {!hasAccount && (
                            this.renderInitialConnectionActions()
                        )}

                        {/* Account exists but not yet active (pending, unknown status, etc.) */}
                        {hasAccount && status !== 'active' && status !== 'restricted' && (
                            <div>
                                <p className="text-warning mb-2" style={{fontSize: '0.85rem'}}>
                                    {this.props.t('stripe.incomplete')}
                                </p>
                                {this.renderResendButtons()}
                                {this.renderCancelButton()}
                            </div>
                        )}

                        {/* Active — full controls */}
                        {status === 'active' && (
                            <div>
                                <div className="d-flex align-items-center mb-2">
                                    <label className="mb-0 mr-2" style={{fontSize: '0.85rem'}}>
                                        {this.props.t('stripe.autoPayout')}
                                    </label>
                                    <Button
                                        color={tipPayoutEnabled ? "success" : "secondary"}
                                        size="sm"
                                        onClick={() => this.props.onTogglePayout(user.UserId, !tipPayoutEnabled)}
                                    >
                                        {tipPayoutEnabled ? this.props.t('stripe.enabled') : this.props.t('stripe.disabledBtn')}
                                    </Button>
                                </div>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                    <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => this.props.onViewDashboard(user.UserId)}
                                    >
                                        <i className="zmdi zmdi-open-in-new mr-1"></i>
                                        {this.props.t('stripe.viewDashboard')}
                                    </Button>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        onClick={() => {
                                            if (window.confirm(this.props.t('stripe.removeConfirm'))) {
                                                this.props.onDisconnect(user.UserId);
                                            }
                                        }}
                                    >
                                        {this.props.t('stripe.disconnect')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Restricted — needs attention on Stripe */}
                        {status === 'restricted' && (
                            <div>
                                <p className="text-danger mb-2" style={{fontSize: '0.85rem'}}>
                                    {this.props.t('stripe.restrictedHelp')}
                                </p>
                                <Button
                                    color="warning"
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => (this.props.onFixOnStripe || this.props.onViewDashboard)(user.UserId)}
                                >
                                    {this.props.t('stripe.fixOnStripe')}
                                </Button>
                                {this.renderResendButtons()}
                                {this.renderCancelButton()}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation(StripeConnectBadge);
